import { useCallback, useMemo } from 'react';
import { useAnalysisContext } from '../context/AnalysisContext';

/**
 * Hook for managing the analysis workflow navigation and state
 * Provides convenience methods for step transitions and validation
 */
export const useAnalysisWorkflow = () => {
  const context = useAnalysisContext();

  // ========================================
  // STEP INFORMATION
  // ========================================

  // Determine if dataset selection step should be shown
  const shouldShowDatasetStep = context.availableDatasets.length > 1;

  const steps = useMemo(
    () => {
      const baseSteps = [
        {
          id: 1,
          label: 'Select Disease',
          shortLabel: 'Disease',
          description: 'Choose the disease context for analysis',
        },
        {
          id: 2,
          label: 'Select Cell Types',
          shortLabel: 'Cells',
          description: 'Choose disease and control cell populations',
        },
        {
          id: 3,
          label: 'Configure Analysis',
          shortLabel: 'Analysis',
          description: 'Set analysis type and parameters',
        },
        {
          id: 4,
          label: 'View Results',
          shortLabel: 'Results',
          description: 'Explore analysis results and export data',
        },
      ];

      // Insert Step 1.5 (Dataset Selection) if multiple datasets are available
      if (shouldShowDatasetStep) {
        baseSteps.splice(1, 0, {
          id: 1.5 as any, // Type workaround for fractional step
          label: 'Select Dataset',
          shortLabel: 'Dataset',
          description: 'Choose which dataset to analyze',
        });
      }

      return baseSteps;
    },
    [shouldShowDatasetStep]
  );

  const currentStepInfo = useMemo(
    () => steps.find((s) => s.id === context.currentStep),
    [steps, context.currentStep]
  );

  // ========================================
  // STEP STATUS
  // ========================================

  const getStepStatus = useCallback(
    (stepId: number): 'completed' | 'active' | 'upcoming' | 'disabled' => {
      if (context.completedSteps.includes(stepId)) {
        return 'completed';
      }
      if (stepId === context.currentStep) {
        return 'active';
      }
      if (context.canNavigateToStep(stepId)) {
        return 'upcoming';
      }
      return 'disabled';
    },
    [context.completedSteps, context.currentStep, context.canNavigateToStep]
  );

  // ========================================
  // NAVIGATION HELPERS
  // ========================================

  const canProceed = useMemo(() => {
    const validation = context.validateCurrentStep();
    console.log('[useAnalysisWorkflow] canProceed check:', {
      currentStep: context.currentStep,
      selectedDisease: context.selectedDisease,
      validation,
    });
    return validation.valid;
  }, [context.validateCurrentStep, context.currentStep, context.selectedDisease, context.selectedCellTypes]);

  const canGoBack = useMemo(() => {
    return context.currentStep > 1;
  }, [context.currentStep]);

  const proceedToNextStep = useCallback(() => {
    const validation = context.validateCurrentStep();
    if (!validation.valid) {
      return {
        success: false,
        errors: validation.errors,
      };
    }

    context.goToNextStep();
    return {
      success: true,
      errors: [],
    };
  }, [context]);

  const returnToPreviousStep = useCallback(() => {
    if (canGoBack) {
      context.goToPreviousStep();
    }
  }, [canGoBack, context]);

  const jumpToStep = useCallback(
    (stepId: number) => {
      if (context.canNavigateToStep(stepId)) {
        context.setCurrentStep(stepId as 1 | 2 | 3 | 4 | 5);
        return true;
      }
      return false;
    },
    [context]
  );

  // ========================================
  // PROGRESS TRACKING
  // ========================================

  const progress = useMemo(() => {
    const totalSteps = steps.length;
    const completedCount = context.completedSteps.length;
    const percentage = (completedCount / totalSteps) * 100;

    return {
      current: context.currentStep,
      total: totalSteps,
      completed: completedCount,
      percentage,
      isComplete: completedCount === totalSteps,
    };
  }, [context.currentStep, context.completedSteps, steps.length]);

  // ========================================
  // WORKFLOW STATE
  // ========================================

  const workflowState = useMemo(() => {
    return {
      hasSelectedDisease: !!context.selectedDisease,
      hasSelectedCellTypes:
        context.selectedCellTypes.disease.length > 0 ||
        context.selectedCellTypes.normal.length > 0,
      hasConfiguredAnalysis: !!context.selectedAnalysisType,
      hasResults: !!context.analysisResult,
      isAnalyzing: context.isAnalyzing,
      hasError: !!context.analysisError,
    };
  }, [
    context.selectedDisease,
    context.selectedCellTypes,
    context.selectedAnalysisType,
    context.analysisResult,
    context.isAnalyzing,
    context.analysisError,
  ]);

  // ========================================
  // RETURN VALUE
  // ========================================

  return {
    // Step information
    steps,
    currentStep: context.currentStep,
    currentStepInfo,
    shouldShowDatasetStep,

    // Step status
    getStepStatus,

    // Navigation
    canProceed,
    canGoBack,
    proceedToNextStep,
    returnToPreviousStep,
    jumpToStep,

    // Progress
    progress,

    // State
    workflowState,

    // Direct context access
    context,
  };
};

export default useAnalysisWorkflow;
